/**
 * videoPlayerEventHandlers.ts
 *
 * Factory that creates event handlers for `VideoPlayer` fw:* events.
 * - Purpose: keep analytics-building and tracker-init logic out of the
 *   React component so `VideoPlayer.tsx` remains an orchestrator only.
 * - Exports: `createVideoPlayerHandlers(ctx)` which returns a map of
 *   fw:* event listeners (play, pause, mute, unmute, fullscreen, start,
 *   impression, etc.).
 * - Consumers: `apps/bfl-live-commerce-app/libs/src/components/VideoPlayer/VideoPlayer.tsx`
 *
 * The handlers accept a lightweight context (analytics builders,
 * tracker init/destroy, helpers) and call `dataLayer`/CleverTap helpers
 * via the provided builder functions.  This file is intentionally
 * side-effect free and safe to unit-test in isolation.
 */

import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import type { EnrichVideoPlayerCT } from "../livecommerceCleverTap";

type Video = Record<string, unknown>;

export type VideoPlayerHandlerContext = {
  sectionTitle: string;
  videoId: string;
  creatorName?: string;
  eventName?: string;
  enableTracking?: boolean;
  NR_VIDEO_LOG?: string;
  // helpers provided by VideoPlayer
  resolveVideoTitle: (detail?: unknown) => string;
  resolveApiVideoMetaCallback: (detail?: unknown) => {
    videoId?: string;
    contentTitle?: string;
    liveStreamStatus?: string;
  };
  resolveLivestreamStatus: (detail?: unknown) => string;
  initVideoTracker: (el: HTMLElement) => void;
  destroyTracker: (reason: string) => void;
  buildVideoPlayerAnalyticsPayload: (opts: {
    eventType: string;
    ctaText: string;
    sectionTitle: string;
    videoIdFallback: string;
    creatorName?: string;
    eventName?: string;
    video?: Video;
    eventNameKey?: string;
  }) => { data: Record<string, string>; cleverTapObject?: Record<string, string> };
  enrichVideoPlayerCT: EnrichVideoPlayerCT;
};

export function createVideoPlayerHandlers(ctx: VideoPlayerHandlerContext) {
  const {
    sectionTitle,
    videoId,
    creatorName,
    eventName,
    NR_VIDEO_LOG,
    initVideoTracker,
    destroyTracker,
    buildVideoPlayerAnalyticsPayload,
  } = ctx;

  const handleVideoPlay: EventListener = (event: Event) => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as { video?: Video };
    const { data, cleverTapObject } = buildVideoPlayerAnalyticsPayload({
      eventType: customEvent?.type || "",
      ctaText: "PLAY",
      sectionTitle,
      videoIdFallback: videoId,
      creatorName,
      eventName,
      video: videoDetails?.video,
      eventNameKey: dlElementConstants.VIDEO_CLICKED,
    });

    dataLayer("", data, cleverTapObject);
  };

  const handleVideoPause: EventListener = (event: Event) => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as { video?: Video };
    const { data, cleverTapObject } = buildVideoPlayerAnalyticsPayload({
      eventType: customEvent?.type || "",
      ctaText: "PAUSE",
      sectionTitle,
      videoIdFallback: videoId,
      creatorName,
      eventName,
      video: videoDetails?.video,
    });

    dataLayer("", data, cleverTapObject);
  };

  const handleVideoFullscreen: EventListener = (event: Event) => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as { video?: Video };
    const video = videoDetails?.video;

    const { data, cleverTapObject } = buildVideoPlayerAnalyticsPayload({
      eventType: customEvent?.type || "",
      ctaText: "FULLSCREEN",
      sectionTitle,
      videoIdFallback: videoId,
      creatorName,
      eventName,
      video,
    });

    dataLayer("", data, cleverTapObject);
  };

  const handlePlayerQuit: EventListener = () => {
    destroyTracker("fw:player:quit");
  };

  const handleVideoImpression: EventListener = () => {
    // try to init tracker if host provided by consumer
    // caller (VideoPlayer) will provide initVideoTracker that includes retry logic
    // here we simply call it when available
    try {
      const storyBlock =
        (document.querySelector(`fw-storyblock[name="player-${videoId}"]`) as HTMLElement) || null;
      if (storyBlock) {
        initVideoTracker(storyBlock);
      }
    } catch (error) {
      if (NR_VIDEO_LOG) {
        console.warn(NR_VIDEO_LOG, "storyblock not found on impression", { error, videoId });
      }
    }
  };

  const handleVideoStart: EventListener = () => {
    try {
      const storyBlock =
        (document.querySelector(`fw-storyblock[name="player-${videoId}"]`) as HTMLElement) || null;
      if (storyBlock) {
        initVideoTracker(storyBlock);
      }
    } catch (error) {
      if (NR_VIDEO_LOG) {
        console.warn(NR_VIDEO_LOG, "storyblock not found on video start", { error, videoId });
      }
    }
  };

  return {
    handleVideoStart,
    handleVideoPlay,
    handleVideoPause,
    handleVideoFullscreen,
    handleVideoImpression,
    handlePlayerQuit,
  };
}
